import React from 'react';
import { Field, FormSection } from 'redux-form';
import moment from 'moment';
import { addValidator, required } from 'redux-form-validators';
import {
  DatePickerDob2,
  HorizontalFormControl,
} from '../../../../../../../../../common/components';
import PostcodeList from '../../../../../../../../../common/components/Postcode/PostcodeList';
import {
  AddressFields,
  SelectField,
} from '../../../../../../../../../common/components/ReduxFormField';
import NameFields from '../../../../NameFields/NameFields';
import './Insured.scss';
import { isAfter, isBefore, isValid, parse, subYears } from 'date-fns';
import { MarketingCheckboxField } from '../../../../../../../../../common/components/MarketingOptions/MarketingOptions';

const cleanNumber = (value) => {
  return value ? value.replace(/[^0-9]+/g, '') : '';
};

const DOB_RULES = {
  HCP: {
    minAge: 18,
    maxAge: 65,
  },
  CC: {
    minAge: 18,
    maxAge: 70,
  },
  PA: {
    minAge: 18,
    maxAge: 75,
  },

  SC: {
    minAge: 18,
    maxAge: 65,
  },
};

const dobValidator = addValidator({
  validator: function(options, value) {
    const { minAge, maxAge } = options;
    const formattedDate = parse(value, 'dd/MM/yyyy', new Date());
    if (!isValid(formattedDate)) {
      return {
        id: 'form.errors.custom',
        defaultMessage: 'DOB is required',
      };
    }
    const minDateAllowed = subYears(new Date(), maxAge);
    const maxDateAllowed = subYears(new Date(), minAge);

    if (!isAfter(formattedDate, minDateAllowed)) {
      return {
        id: 'form.errors.custom',
        defaultMessage: 'must be under ' + maxAge + ' years old',
      };
    }
    if (!isBefore(formattedDate, maxDateAllowed)) {
      return {
        id: 'form.errors.custom',
        defaultMessage: 'must be over ' + minAge + ' years old',
      };
    }
  },
});

const LeadInsured = ({ input, productCode, maxAge, minAge }) => {
  const { postcode } = input.value.address;
  return (
    <div className={'pa-form-section'}>
      <h2 className={'sub-title'}>Lead Insured</h2>
      <NameFields
        baseName={'adults.0'}
        label="Name"
        labelSize={2}
        fieldSize={6}
        isChild={false}
      />
      <Field
        name={'gender'}
        labelSize={2}
        smFieldSize={3}
        label="Gender"
        options={[
          {
            value: 'female',
            label: 'Female',
          },
          {
            value: 'male',
            label: 'Male',
          },
          {
            value: 'prefer_not_to_say',
            label: 'Prefer not to say',
          },
        ]}
        component={SelectField}
      />
      {productCode === 'CC' && (
        <Field
          name={'adults.0.is_smoker'}
          labelSize={2}
          smFieldSize={3}
          label="Are you a smoker"
          options={[
            {
              value: 'non-smoker',
              label: 'No, never smoked',
            },
            {
              value: 'smoker',
              label: "Yes I'm a smoker",
            },
            {
              value: 'non-smoker-gt2-years',
              label: 'Ex smoker – 2 or more years',
            },
            {
              value: 'non-smoker-lt2-years',
              label: 'Ex smoker – less than 2 years',
            },
          ]}
          component={SelectField}
        />
      )}
      <FormSection name={'adults.0'}>
        <Field
          name={'phone1'}
          labelSize={2}
          fieldSize={3}
          parse={cleanNumber}
          label="Contact telephone"
          placeholder="Contact telephone"
          component={HorizontalFormControl}
          // validate={[required({ msg: 'Telephone is required' }), numericality({ int: true, msg: 'Not a valid phone number' })]}
        />
        <Field
          name={'email'}
          labelSize={2}
          fieldSize={3}
          type="email"
          label="Contact email address"
          placeholder="Enter email address"
          component={HorizontalFormControl}
          // validate={[required({ msg: 'Email is required' }), email({ msg: 'Please enter a valid email' })]}
        />

        {/*  <Col sm={2} className="dob-input-col"> */}
        <Field
          name={'dob'}
          showYear
          minDate={moment()
            .subtract(120, 'years')
            .format()}
          maxDate={moment()
            .subtract(18, 'years')
            .format()}
          component={DatePickerDob2}
          label="Date of birth"
          labelSize={2}
          componentSize={3}
          validate={dobValidator({
            msg: 'dob is required',
            product: productCode,
            maxAge: maxAge,
            minAge: minAge,
          })}
        />

        <PostcodeList
          name={'address.postcode'}
          label="Postcode"
          labelSize={2}
          searchSize={2}
          postcodeFieldSize={2}
          smFieldSize={7}
          formName="PersonalAccidentForm"
          value={postcode}
          targetField={'data.attributes.metadata.adults.0.address'}
          validate={required({ msg: 'postcode is required' })}
        />

        <AddressFields
          baseName={'address'}
          label="Address"
          labelSize={2}
          smFieldSize={4}
          disabled={false}
        />
        <Field
          name="marketing_options.email"
          label={
            <div className={'marketing-options--label'}>
              American International Group UK Limited would like to send you information
              about other AIG products that we think would be of interest. I would like to
              receive this information by email.
            </div>
          }
          labelSize={6}
          smFieldSize={2}
          component={MarketingCheckboxField}
        />
      </FormSection>
    </div>
  );
};

const Partner = ({ productCode, maxAge, minAge }) => {
  return (
    <div className={'pa-form-section'}>
      <h2 className={'sub-title'}>Partner Details</h2>
      <NameFields baseName={'adults.1'} label="Name" labelSize={2} fieldSize={3} />
      <Field
        name={'adults.1.dob'}
        showYear
        minDate={moment()
          .subtract(120, 'years')
          .format()}
        maxDate={moment()
          .subtract(18, 'years')
          .format()}
        component={DatePickerDob2}
        label="Date of birth"
        labelSize={2}
        componentSize={3}
        validate={dobValidator({
          msg: 'dob is required',
          product: productCode,
          maxAge: maxAge,
          minAge: minAge,
        })}
      />
      {productCode === 'CC' && (
        <>
          <Field
            name={'adults.1.gender'}
            labelSize={2}
            smFieldSize={2}
            label="Gender"
            options={[
              {
                value: 'female',
                label: 'Female',
              },
              {
                value: 'male',
                label: 'Male',
              },
              {
                value: 'prefer_not_to_say',
                label: 'Prefer not to say',
              },
            ]}
            component={SelectField}
          />
          <Field
            name={'adults.1.is_smoker'}
            labelSize={2}
            smFieldSize={3}
            label="Are you a smoker"
            options={[
              {
                value: 'non-smoker',
                label: 'No, never smoked',
              },
              {
                value: 'smoker',
                label: "Yes I'm a smoker",
              },
              {
                value: 'non-smoker-gt2-years',
                label: 'Ex smoker – 2 or more years',
              },
              {
                value: 'non-smoker-lt2-years',
                label: 'Ex smoker – less than 2 years',
              },
            ]}
            component={SelectField}
          />
        </>
      )}
    </div>
  );
};

const NumChildren = () => {
  const options = [...Array(10).keys()].map((index) => ({
    label: `${index + 1}`,
    value: index + 1,
  }));
  return (
    <div className={'pa-form-section'}>
      <Field
        name={'num_children'}
        labelSize={2}
        label="Number of Children"
        options={options}
        component={SelectField}
        smFieldSize={2}
      />
    </div>
  );
};

const Individual = ({ productCode, rules }) => (
  <div>
    <Field
      name={'adults.0'}
      component={LeadInsured}
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
  </div>
);
const Couple = ({ productCode, rules }) => (
  <div>
    <Field
      name={'adults.0'}
      component={LeadInsured}
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
    <Partner
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
  </div>
);
const Spf = ({ productCode, rules }) => (
  <div>
    <Field
      name={'adults.0'}
      component={LeadInsured}
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
    <NumChildren />
  </div>
);

const Family = ({ productCode, rules }) => (
  <div>
    <Field
      name={'adults.0'}
      component={LeadInsured}
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
    <Partner
      productCode={productCode}
      maxAge={rules?.adult_max_age ?? DOB_RULES[productCode].maxAge}
      minAge={rules?.adult_min_age ?? DOB_RULES[productCode].minAge}
    />
    <NumChildren />
  </div>
);
const InsuredComponentMap = {
  individual: Individual,
  couple: Couple,
  spf: Spf,
  family: Family,
};

const Insured = ({ groupType, productCode, rules }) => {
  if (!groupType) {
    return null;
  }

  const InsuredComp = InsuredComponentMap[groupType];
  return <InsuredComp productCode={productCode} rules={rules} />;
};

export default Insured;
